import React, { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import BootstrapTable, {
  BootstrapSortDirection,
  IBootstrapTableColumnProps,
  IDataRow,
  TableChangeEventHandler,
  TBootstrapHeaderFormatter,
  TBootstrapRowClassesFunction,
} from 'react-bootstrap-table-next';
import filterFactory from 'react-bootstrap-table2-filter';
import paginationFactory from 'react-bootstrap-table2-paginator';
import styled, { css } from 'styled-components';
import { Cover } from '../Cover';
import { Spinner, SpinnerSize } from '../Spinner';
import { Icon, IconSize, IconType } from '../Icon';
import { BodyText, BoldSmallBodyText } from '../Typography';
import { PaginationTotal } from './PaginationTotal';
import { SizePerPage } from './SizePerPage';
import { FilterRendererFactory } from './FilterRendererFactory';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { PageListRenderer } from './PageListRenderer';
import { mergeTests } from '../../test-utilities/mergeTests';
import { IHasTestData } from '../../test-utilities/IHasTestData';

const headerFilterCellHeightV2 = '54px';
const headerNameCellHeightV2 = '32px';

const WrapperCssV2 = css`
  table {
    width: 100%;
    border-collapse: collapse;
    th {
      background-color: ${(props) => props.theme.colors.lightGray};
      padding: 0;
    }
    tr {
      box-shadow: inset 0px -2px 0px ${(props) => props.theme.colors.gray};
    }
    td {
      padding: ${(props) => props.theme.spacing.spacing4} ${(props) => props.theme.spacing.spacing2};
    }
    tbody tr:nth-child(odd) {
      background-color: ${(props) => props.theme.colors.defaultContentBackground};
    }
    tbody tr:nth-child(even) {
      background-color: ${(props) => props.theme.colors.lightGray};
    }
    tbody tr:hover {
      background-color: ${(props) => props.theme.colors.gray50};
    }
    tbody tr.selected {
      background-color: ${(props) => props.theme.colors.gray50};
    }
  }
`;

export const StyledWrapper = styled(BodyText)`
  ${WrapperCssV2};
`;

const HeaderCssV2 = css<{ gridPreset: string }>`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing4};
  grid-template: ${(props) =>
    props.gridPreset === 'filtersort'
      ? `'filter filter' ${headerFilterCellHeightV2} 'name sort' ${headerNameCellHeightV2} / max-content 1fr`
      : props.gridPreset === 'filter'
        ? `'filter' ${headerFilterCellHeightV2} 'name' ${headerNameCellHeightV2}`
        : props.gridPreset === 'sort'
          ? `'name sort' ${headerNameCellHeightV2} / max-content 1fr`
          : `'name' ${headerNameCellHeightV2}`};
  align-items: center;
  align-content: center;
  white-space: nowrap;
  > * {
    display: flex;
    ${(props) => (props.gridPreset.includes('sort') ? 'cursor: pointer;' : '')};
  }
`;

const StyledHeader = styled(BoldSmallBodyText)<{ gridPreset: string }>`
  ${HeaderCssV2};
`;

const HeaderNameCssV2 = css`
  align-items: center;
  display: flex;
  grid-area: name;
  height: ${headerNameCellHeightV2};
  padding: ${(props) => props.theme.spacing.spacing4} ${(props) => props.theme.spacing.spacing2};
`;

const StyledHeaderName = styled.div`
  ${HeaderNameCssV2};
`;

const HeaderSortCssV2 = css`
  align-items: center;
  justify-content: start;
  display: flex;
  grid-area: sort;
  height: ${headerNameCellHeightV2};
  margin-left: ${(props) => props.theme.spacing.spacing2};
`;

const StyledHeaderSort = styled.div`
  ${HeaderSortCssV2};
`;

const HeaderFilterCssV2 = css`
  align-items: center;
  box-shadow: inset 0px -2px 0px ${(props) => props.theme.colors.gray};
  display: flex;
  grid-area: filter;
  height: ${headerFilterCellHeightV2};
  padding: ${(props) => props.theme.spacing.spacing4} ${(props) => props.theme.spacing.spacing2};
`;

const StyledHeaderFilter = styled.div`
  ${HeaderFilterCssV2};
`;

const StyledNoDataWrapper = styled.div`
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export type TDataGridSortDirection = 'asc' | 'desc';

export interface ISortedBy {
  dataField: string;
  order: TDataGridSortDirection;
}

export interface IDataGridProps<T extends object = {}> extends IHasTestData {
  /** Additional CSS class. */
  className?: string;
  /** List of columns. */
  columns: IBootstrapTableColumnProps<T>[];
  /** The collection of items to render in the table. This is an array of objects with each object having keys that correspond with the property 'columns'. */
  data?: IDataRow<T>[];
  /** Default Sort to apply to data when it is first set */
  defaultSorted?: ISortedBy[];
  /** Default Sort Direction to apply when user first selects the sort column */
  defaultSortDirection?: TDataGridSortDirection;
  /** Provide a renderer for when a row is expanded */
  expandRow?: { renderer: (row: T) => ReactNode };
  /** Add this to enable filtering */
  filter?: boolean;
  /** Unique element key selector in the "data" collection. */
  keyField: string;
  /** If true, loading spinner appears on the entire table. */
  loading?: boolean;
  /** Content to display when there is no data */
  noData?: React.ReactNode;
  /** Add this if you intended to set "remote={true}" */
  onTableChange?: TableChangeEventHandler<T>;
  /** Set pagination state such as current page and total number of records */
  pagination?: { page: number; sizePerPage: number; totalSize: number };
  /** Add this to enable remote query support */
  remote?: boolean;
  /** Set class names or a function that returns classes to attach to each row (tr) `.selected` will highlight the way green. */
  rowClasses?: string | TBootstrapRowClassesFunction<T>;
  /** Add event handlers for rows */
  rowEvents?: { onClick?: (e: React.SyntheticEvent, row: any, rowIndex: number) => void };
  /** Provide a config for how row select should work */
  selectRow?: {
    mode: 'checkbox' | 'radio';
    clickToSelect?: boolean;
    onSelect?: (row: IDataRow<T>, isSelect: boolean, rowIndex: number, e: any) => boolean | void;
    hideSelectColumn?: boolean;
  };
  /** Set the current sort of the data grid */
  sort?: { dataField: string; order: TDataGridSortDirection };
  /** Optional z-index value for the spinner container */
  zIndex?: string;
}

export const DefaultNoData = <Icon type={IconType.Search} size={IconSize.Regular} />;
export const NoDataWrapper: React.FC<{ loading: boolean; noData: React.ReactNode }> = ({ loading, noData }) =>
  loading ? null : <StyledNoDataWrapper>{noData || DefaultNoData}</StyledNoDataWrapper>;

export const headerRenderer: <T extends object = {}>(filter?: boolean) => TBootstrapHeaderFormatter<T> =
  (filter) =>
  (column, colIdx, { sortElement, filterElement }) => (
    <StyledHeader
      gridPreset={`${filter ? 'filter' : ''}${sortElement ? 'sort' : ''}`}
      style={{ whiteSpace: (column?.headerStyle as CSSProperties)?.whiteSpace }}
    >
      <StyledHeaderName>{column.text}</StyledHeaderName>
      {sortElement ? <StyledHeaderSort>{sortElement}</StyledHeaderSort> : null}
      {filter ? <StyledHeaderFilter>{filterElement}</StyledHeaderFilter> : null}
    </StyledHeader>
  );

const iconFromOrder = (order: BootstrapSortDirection) => {
  switch (order) {
    case 'asc':
      return faArrowUp;
    case 'desc':
      return faArrowDown;
    default:
      return false;
  }
};

export const mergeColumnDefaults = <T extends object = {}>(
  columns: IBootstrapTableColumnProps<T>[] = [],
  filter?: boolean
): IBootstrapTableColumnProps<T>[] =>
  columns.map((col) => {
    return {
      ...col,
      headerFormatter: col.headerFormatter || headerRenderer(filter),
      sortCaret: (order) => {
        const icon = iconFromOrder(order);
        return (icon && <FontAwesomeIcon icon={icon} />) || <></>;
      },
      filterRenderer: col.filterRenderer ?? (col.filter ? FilterRendererFactory(col.filter) : undefined),
    };
  });

/**
 * DataGrid support the display of structured data in rows and columns with an HTML table.
 */
export const DataGrid = <T extends object = {}>({
  className,
  columns,
  data,
  defaultSorted,
  defaultSortDirection,
  expandRow,
  filter,
  keyField,
  loading,
  noData,
  onTableChange,
  pagination,
  remote,
  rowClasses,
  rowEvents,
  selectRow,
  sort,
  'data-test-id': dataTestId,
  'data-test-class': dataTestClass,
  zIndex,
}: PropsWithChildren<IDataGridProps<T>>) => {
  const computedColumns = mergeColumnDefaults(columns, filter);
  const getRowClasses = (row: any, rowIndex: number) => {
    if (typeof rowClasses === 'function') {
      return rowClasses(row, rowIndex);
    }
    return rowClasses || mergeTests(dataTestClass || 'data-grid-row', row[keyField] || rowIndex);
  };
  return (
    <StyledWrapper className={className}>
      <BootstrapTable
        columns={computedColumns}
        data={data}
        defaultSorted={defaultSorted}
        defaultSortDirection={defaultSortDirection || 'asc'}
        expandRow={expandRow}
        filter={filter ? filterFactory({}) : undefined}
        keyField={keyField}
        loading={loading}
        noDataIndication={() => <NoDataWrapper loading={!!loading} noData={noData} />}
        onTableChange={onTableChange}
        data-test-class={dataTestClass || 'data-grid'}
        pagination={
          pagination
            ? paginationFactory({
                ...pagination,
                hideSizePerPage: true,
                pageListRenderer: PageListRenderer,
                paginationTotalRenderer: (from, to, size) => <PaginationTotal {...{ from, to, size }} />,
                showTotal: false,
                sizePerPageRenderer: SizePerPage,
              })
            : undefined
        }
        remote={remote}
        rowEvents={rowEvents}
        rowClasses={getRowClasses as any}
        selectRow={selectRow}
        sort={sort}
      />
      {/* FYI: BootstrapTable's overlay support causes infinite refresh because rendering a filter triggers a filter event, which triggers a re-renders, which triggers a filter event. so on and so forth... */}
      {loading && (
        <Cover zIndex={zIndex}>
          <Spinner size={SpinnerSize.regular} data-test-class={mergeTests(dataTestClass || 'data-grid', 'spinner')} />
        </Cover>
      )}
    </StyledWrapper>
  );
};
